import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import '../../DashboardHome/rush.css';
import '../../DashboardHome/dashboard.css';
import '../../DashboardHome/rush-fixes.css';

const EIcn = {
    back: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M8 3L4 7l4 4M4 7h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    caret: (
        <svg className="caret" width="9" height="9" viewBox="0 0 10 10" fill="none">
            <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    check: (
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    edit: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M2 11l2-.5L11 4l-1.5-1.5L3 9l-1 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    ),
    reject: (
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    ai: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M8 2l1.4 3.1L12.5 6 9.4 7.4 8 10.5 6.6 7.4 3.5 6l3.1-.9L8 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
    ),
    memory: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M8 5v3l2 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
    glossary: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M3 3h7l3 3v7H3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M5 7h6M5 9.5h6M5 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
    comment: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M2.5 8a5 5 0 015-5h1a5 5 0 015 5v3a1 1 0 01-1 1H8L5 14v-2H3.5a1 1 0 01-1-1V8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    ),
    alert: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M8 2L1.5 13h13L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M8 6v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="8" cy="11" r="0.7" fill="currentColor" />
        </svg>
    ),
    okCirc: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
            <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    warn: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M8 2L1.5 13h13L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M8 6v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="8" cy="11" r="0.7" fill="currentColor" />
        </svg>
    ),
    filter: (
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M2 3h10l-4 5v4l-2-1V8L2 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    ),
};

const STATUS_LABEL = {
    approved: 'Approved',
    reviewed: 'Reviewed',
    translated: 'Translated',
    flagged: 'Flagged',
    untranslated: 'Untranslated',
};

const STATUS_FILTERS = ['All', 'Untranslated', 'Translated', 'Reviewed', 'Approved', 'Flagged'];

const segStatus = (i, translated, approved, confidence) => {
    if ((approved || []).includes(i)) return 'approved';
    const t = translated[i];
    if (!t || !t.trim()) return 'untranslated';
    const c = confidence && confidence[i];
    if (c != null && c < 60) return 'flagged';
    if (c != null && c >= 95) return 'reviewed';
    return 'translated';
};

const tmCls = (pct) => {
    if (pct == null) return 'low';
    if (pct >= 90) return '';
    if (pct >= 75) return 'med';
    return 'low';
};

const initialsOf = (n) => (n || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

// ============ TOP BAR ============
const TopBar = ({ project, approvedCount, total, current, sourceLang, targetLang, saving, onBack }) => {
    const pct = total ? Math.round((approvedCount / total) * 100) : 0;
    const e = (() => {
        const fname = (project.fileName || project.fileUrl || '').toString();
        const m = /\.([a-z0-9]+)$/i.exec(fname);
        return ((m ? m[1] : 'doc')).slice(0, 3).toUpperCase();
    })();
    return (
        <div className="rs-ed-bar">
            <button type="button" className="back" onClick={onBack}>
                {EIcn.back}<span>Back</span>
            </button>
            <div className="file-ic">{e}</div>
            <div className="title-block">
                <div className="pn">{project.name}</div>
                <div className="crm">{project.category || 'Project'} · {total} segments</div>
            </div>
            <div className="rs-ed-lang-switch">
                <span className="pair">{sourceLang} <span className="arr">→</span> {targetLang}</span>
                {EIcn.caret}
            </div>
            <div className="rs-ed-progress">
                <div className="pmeta">
                    <span>Segment <b>{(current ?? 0) + 1}</b> / <b>{total}</b></span>
                    <span><b>{pct}%</b> complete</span>
                </div>
                <div className="pbar"><span style={{ width: `${pct}%` }} /></div>
            </div>
            <span className="spacer" />
            <span className="save-status">
                <span className="dt" style={{ background: saving ? 'var(--accent)' : 'var(--green)' }} />
                {saving ? 'Saving…' : 'All changes saved'}
            </span>
        </div>
    );
};

// ============ NAVIGATOR ============
const Navigator = ({ sections, translated, approved, confidence, active, setActive, filter, setFilter }) => {
    const items = sections.map((src, i) => ({
        src,
        i,
        status: segStatus(i, translated, approved, confidence),
    }));
    const filtered = filter === 'All' ? items : items.filter((it) => STATUS_LABEL[it.status] === filter);

    return (
        <aside className="rs-ed-snav">
            <div className="rs-ed-snav-h">
                <h3>Segments — {filtered.length}</h3>
                <div className="rs-ed-snav-filters">
                    {STATUS_FILTERS.map((f) => (
                        <button
                            key={f}
                            type="button"
                            className={`rs-ed-snav-filter ${filter === f ? 'on' : ''}`}
                            onClick={() => setFilter(f)}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>
            <div className="rs-ed-snav-list">
                {filtered.length === 0 && (
                    <div style={{ padding: 24, color: 'var(--ink-4)', fontSize: 12.5, textAlign: 'center' }}>
                        Nothing matches this filter.
                    </div>
                )}
                {filtered.map(({ src, i, status }) => (
                    <button
                        type="button"
                        key={i}
                        className={`rs-ed-snav-item ${active === i ? 'active' : ''}`}
                        onClick={() => setActive(i)}
                    >
                        <span className="sn">{i + 1}</span>
                        <span className="stxt">{src.slice(0, 80)}</span>
                        <span className={`sdot ${status}`} />
                    </button>
                ))}
            </div>
        </aside>
    );
};

// ============ GLOSSARY HIGHLIGHTING ============
const renderWithGloss = (text, glossList = []) => {
    if (!glossList.length || !text) return text;
    const escaped = glossList.map((g) => g.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'));
    const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
    return text.split(pattern).map((p, i) => {
        const hit = glossList.some((g) => g.toLowerCase() === p.toLowerCase());
        return hit
            ? <span key={i} className="gloss" title="Glossary term">{p}</span>
            : <React.Fragment key={i}>{p}</React.Fragment>;
    });
};

// ============ ACTIVE SEGMENT ============
const ActiveSegment = ({
    index, total, source, target, isApproved, confidence,
    sourceLang, targetLang,
    glossTerms,
    aiSuggestion, aiSourceLabel, aiLoading,
    onChange, onSave, onApprove, onUnapprove, onReject,
    onAcceptAI, onDismissAI, onRegenerateAI,
}) => {
    const [val, setVal] = useState(target || '');
    const taRef = useRef(null);

    useEffect(() => {
        setVal(target || '');
    }, [target, index]);

    useEffect(() => {
        if (taRef.current) taRef.current.focus();
    }, [index]);

    const status = isApproved ? 'approved' : (val ? 'translated' : 'untranslated');
    const tmDisplay = confidence != null ? `${Math.round(confidence)}%` : '—';

    const save = useCallback(() => {
        onChange(val);
        onSave(val);
    }, [val, onChange, onSave]);

    return (
        <div className="rs-ed-seg active">
            <div className="a-top">
                <span className="a-num">Segment <b>{index + 1}</b> / {total}</span>
                <span className={`seg-status ${status}`}>{STATUS_LABEL[status]}</span>
                <span className="spacer" />
                <span className="a-meta">
                    <b>{tmDisplay}</b> match
                    <span style={{ width: 3, height: 3, borderRadius: 999, background: 'var(--ink-4)' }} />
                    <b>{sourceLang} → {targetLang}</b>
                </span>
            </div>

            <div className="a-panes">
                <div className="a-pane src">
                    <div className="a-lbl">Source · {sourceLang}</div>
                    <div className="a-text">{renderWithGloss(source, glossTerms.map((g) => g.source))}</div>
                </div>
                <div className="a-pane tgt">
                    <div className="a-lbl">Target · {targetLang}</div>
                    <textarea
                        ref={taRef}
                        className="a-input"
                        value={val}
                        placeholder="Translate this segment…"
                        onChange={(e) => setVal(e.target.value)}
                        onKeyDown={(e) => {
                            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                                e.preventDefault();
                                save();
                                onApprove();
                            }
                        }}
                    />
                </div>
            </div>

            {(aiSuggestion || aiLoading) && (
                <div className="a-ai">
                    <div className="ai-ic">{EIcn.ai}</div>
                    <div className="ai-body">
                        <div className="ai-lbl">
                            AI suggestion · {aiSourceLabel || 'Rush AI'}
                            {aiLoading && <span style={{ marginLeft: 8, color: 'var(--ink-4)' }}>generating…</span>}
                        </div>
                        {aiSuggestion && (
                            <>
                                <div className="ai-txt">{renderWithGloss(aiSuggestion, glossTerms.map((g) => g.source))}</div>
                                <div className="ai-actions">
                                    <button type="button" className="ai-btn primary" onClick={() => { setVal(aiSuggestion); onAcceptAI(aiSuggestion); }}>
                                        {EIcn.check} Replace
                                    </button>
                                    <button type="button" className="ai-btn" onClick={() => setVal((v) => v + (v.endsWith(' ') ? '' : ' ') + aiSuggestion)}>
                                        Insert at cursor
                                    </button>
                                    <button type="button" className="ai-btn" onClick={onRegenerateAI}>
                                        Regenerate
                                    </button>
                                    <button type="button" className="ai-btn" onClick={onDismissAI}>
                                        Dismiss
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className="a-foot">
                <span className="hint"><span className="kbd">⌘</span> + <span className="kbd">⏎</span> confirm & next</span>
                <span className="hint"><span className="kbd">⌘</span> + <span className="kbd">.</span> skip</span>
                <span className="spacer" />
                <button type="button" className="a-btn warn" onClick={onReject}>
                    {EIcn.reject} Reject
                </button>
                {isApproved ? (
                    <button type="button" className="a-btn ghost" onClick={onUnapprove}>
                        Unapprove
                    </button>
                ) : (
                    <button type="button" className="a-btn ghost" onClick={save}>
                        {EIcn.edit} Save draft
                    </button>
                )}
                <button
                    type="button"
                    className="a-btn primary"
                    onClick={() => { save(); onApprove(); }}
                    disabled={!val.trim()}
                >
                    {EIcn.check} Confirm &amp; next
                </button>
            </div>
        </div>
    );
};

// ============ INACTIVE SEGMENT ROW ============
const InactiveSegment = ({ index, source, target, status, tmDisplay, onActivate }) => (
    <button type="button" className="rs-ed-seg" onClick={() => onActivate(index)}>
        <div className="sn-col">
            <span className="sn-n">{index + 1}</span>
            <span className={`sn-dot ${status}`} />
        </div>
        <div className="src-pane">{source}</div>
        <div className={`tgt-pane ${status === 'untranslated' ? 'empty' : ''}`}>
            {status === 'untranslated' ? '—' : target}
        </div>
        <div className="meta-col">
            <span className={`tm-match ${tmCls(tmDisplay)}`}>{tmDisplay != null ? `${Math.round(tmDisplay)}%` : '—'}</span>
            <span className={`seg-status ${status}`}>{STATUS_LABEL[status]}</span>
        </div>
    </button>
);

// ============ SEG LIST ============
const SegList = ({
    sections, translated, approved, confidence,
    active, setActive,
    sourceLang, targetLang,
    glossTerms,
    aiSuggestion, aiLoading,
    onChangeTarget, onSaveTarget, onApprove, onUnapprove, onReject,
    onAcceptAI, onDismissAI, onRegenerateAI,
    nextActive,
}) => {
    const total = sections.length;
    const visibleRange = useMemo(() => {
        const start = Math.max(0, active - 4);
        const end = Math.min(total, active + 5);
        return { start, end };
    }, [active, total]);

    return (
        <main className="rs-ed-main">
            <div className="rs-ed-main-h">
                <div className="h-info">
                    <b>Editing in-context</b>
                    <span>Showing {visibleRange.start + 1}–{visibleRange.end} of {total} · click any to focus</span>
                </div>
                <span className="spacer" />
                <button type="button" className="btn-q">{EIcn.filter}<span>Filter view</span></button>
                <button type="button" className="btn-q">{EIcn.ai}<span>Bulk AI draft</span></button>
            </div>
            <div className="rs-ed-segs">
                {Array.from({ length: visibleRange.end - visibleRange.start }, (_, k) => {
                    const i = visibleRange.start + k;
                    const status = segStatus(i, translated, approved, confidence);
                    if (i === active) {
                        return (
                            <ActiveSegment
                                key={i}
                                index={i}
                                total={total}
                                source={sections[i]}
                                target={translated[i] || ''}
                                isApproved={approved.includes(i)}
                                confidence={confidence[i]}
                                sourceLang={sourceLang}
                                targetLang={targetLang}
                                glossTerms={glossTerms}
                                aiSuggestion={aiSuggestion}
                                aiLoading={aiLoading}
                                onChange={(v) => onChangeTarget(i, v)}
                                onSave={(v) => onSaveTarget(i, v)}
                                onApprove={() => { onApprove(i); nextActive(); }}
                                onUnapprove={() => onUnapprove(i)}
                                onReject={() => onReject(i)}
                                onAcceptAI={(v) => onSaveTarget(i, v)}
                                onDismissAI={onDismissAI}
                                onRegenerateAI={onRegenerateAI}
                            />
                        );
                    }
                    return (
                        <InactiveSegment
                            key={i}
                            index={i}
                            source={sections[i]}
                            target={translated[i] || ''}
                            status={status}
                            tmDisplay={confidence[i]}
                            onActivate={setActive}
                        />
                    );
                })}
            </div>
        </main>
    );
};

// ============ CONTEXT PANEL ============
const ContextPanel = ({
    activeTab, setActiveTab,
    activeIndex,
    tmMatches, tmLoading,
    glossTerms,
    comments, onAddComment,
    issues,
    onApplyTM,
}) => {
    const [commentDraft, setCommentDraft] = useState('');
    const tabs = [
        ['matches', 'Matches', EIcn.memory, tmMatches.length],
        ['glossary', 'Glossary', EIcn.glossary, glossTerms.length],
        ['comments', 'Comments', EIcn.comment, comments.length],
        ['issues', 'Issues', EIcn.alert, issues.filter((i) => i.kind === 'warn' || i.kind === 'err').length],
    ];

    const submitComment = () => {
        const v = commentDraft.trim();
        if (!v) return;
        onAddComment(v);
        setCommentDraft('');
    };

    return (
        <aside className="rs-ed-cx">
            <div className="rs-ed-cx-tabs">
                {tabs.map(([key, name, ic, ct]) => (
                    <button
                        key={key}
                        type="button"
                        className={`rs-ed-cx-tab ${activeTab === key ? 'on' : ''}`}
                        onClick={() => setActiveTab(key)}
                    >
                        {ic}<span>{name}</span>
                        <span className="ct">{ct}</span>
                    </button>
                ))}
            </div>
            <div className="rs-ed-cx-body">
                {activeTab === 'matches' && (
                    <>
                        <div className="rs-ed-cx-sec">
                            Translation memory · {tmLoading ? 'looking up…' : `${tmMatches.length} matches`}
                        </div>
                        {!tmLoading && tmMatches.length === 0 && (
                            <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 12.5 }}>
                                No matches yet for this segment.
                            </div>
                        )}
                        {tmMatches.map((m, i) => (
                            <div key={i} className="rs-ed-tm">
                                <div className="tm-head">
                                    <span className={`tm-m ${tmCls(m.match)}`}>{m.match}%</span>
                                    <span className="tm-origin">
                                        <b>{m.origin || 'Memory'}</b>{m.when ? ` · ${m.when}` : ''}
                                    </span>
                                    <button type="button" className="tm-apply" onClick={() => onApplyTM(m)}>Apply</button>
                                </div>
                                <div className="tm-src">{m.src}</div>
                                <div className="tm-tgt">{m.tgt}</div>
                            </div>
                        ))}
                    </>
                )}

                {activeTab === 'glossary' && (
                    <>
                        <div className="rs-ed-cx-sec">
                            Terms found in segment {activeIndex + 1}
                        </div>
                        {glossTerms.length === 0 && (
                            <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 12.5 }}>
                                No glossary terms match this segment.
                            </div>
                        )}
                        {glossTerms.map((g, i) => (
                            <div key={i} className="rs-ed-gl-item">
                                <div className="term-row">
                                    <span className="term-src">{g.source}</span>
                                    <span className="arr">→</span>
                                    <span className="term-tgt">{g.target || '—'}</span>
                                    <span className="term-lock">{g.locked ? 'LOCKED' : 'PREFERRED'}</span>
                                </div>
                                {g.context && <div className="term-def">{g.context}</div>}
                                {g.glossaryName && <div className="term-meta">{g.glossaryName}</div>}
                            </div>
                        ))}
                    </>
                )}

                {activeTab === 'comments' && (
                    <>
                        <div className="rs-ed-cx-sec">
                            {comments.length} {comments.length === 1 ? 'comment' : 'comments'} on this segment
                        </div>
                        {comments.length === 0 && (
                            <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 12.5 }}>
                                No comments yet.
                            </div>
                        )}
                        {comments.map((c, i) => (
                            <div key={c._id || i} className="rs-ed-cm-item">
                                <div className="ch">
                                    <span className={`av-s av-${(i % 6) + 1}`}>{initialsOf(c.who)}</span>
                                    <b>{c.who}</b>
                                    <span className="when">{c.when}</span>
                                </div>
                                <div className="cb">{c.text}</div>
                            </div>
                        ))}
                        <div className="rs-ed-cm-reply">
                            <input
                                placeholder="Reply to thread…"
                                value={commentDraft}
                                onChange={(e) => setCommentDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') submitComment(); }}
                            />
                            <button
                                type="button"
                                onClick={submitComment}
                                style={{
                                    height: 26, padding: '0 10px', border: 0,
                                    background: 'var(--ink)', color: 'var(--bg)',
                                    borderRadius: 6, fontSize: 11.5, fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Send
                            </button>
                        </div>
                    </>
                )}

                {activeTab === 'issues' && (
                    <>
                        <div className="rs-ed-cx-sec">
                            QA checks on segment {activeIndex + 1}
                        </div>
                        {issues.length === 0 && (
                            <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 12.5 }}>
                                No issues found.
                            </div>
                        )}
                        {issues.map((iss, i) => (
                            <div key={i} className={`rs-ed-iss-item ${iss.kind}`}>
                                <span className="iss-ic">
                                    {iss.kind === 'ok' && EIcn.okCirc}
                                    {iss.kind === 'warn' && EIcn.warn}
                                    {iss.kind === 'err' && EIcn.alert}
                                </span>
                                <div className="iss-body">
                                    <div className="iss-t">{iss.title}</div>
                                    <div className="iss-d">{iss.desc}</div>
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </aside>
    );
};

// ============ FOOT BAR ============
const FootBar = () => (
    <div className="rs-ed-foot">
        <span className="item"><span className="kbd">⌘</span><span className="kbd">⏎</span><span>Confirm &amp; next</span></span>
        <span className="item"><span className="kbd">⌘</span><span className="kbd">↑</span><span className="kbd">↓</span><span>Move</span></span>
        <span className="item"><span className="kbd">⌘</span><span className="kbd">/</span><span>AI suggest</span></span>
        <span className="item"><span className="kbd">⌘</span><span className="kbd">G</span><span>Insert glossary</span></span>
        <span className="item"><span className="kbd">⌘</span><span className="kbd">M</span><span>Apply top TM</span></span>
        <span className="spacer" />
        <span className="bdg-ai"><span className="dot" />Rush AI · active</span>
    </div>
);

// ============ MAIN ============
const RushEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const token = useSelector((s) => s.auth.token);
    const me = useSelector((s) => s.auth.user) || {};

    const [project, setProject] = useState(null);
    const [translated, setTranslated] = useState([]);
    const [approved, setApproved] = useState([]);
    const [confidence, setConfidence] = useState([]);

    const [active, setActive] = useState(0);
    const [filter, setFilter] = useState('All');
    const [activeTab, setActiveTab] = useState('matches');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const [allGlossaryTerms, setAllGlossaryTerms] = useState([]);
    const [tmMatches, setTmMatches] = useState([]);
    const [tmLoading, setTmLoading] = useState(false);
    const [aiSuggestion, setAiSuggestion] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [comments, setComments] = useState({}); // keyed by segment index

    useEffect(() => {
        document.body.classList.add('rs-editor-body');
        return () => document.body.classList.remove('rs-editor-body');
    }, []);

    useEffect(() => {
        if (!token || !id) return;
        axios
            .get(`/api/projects/${id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                const p = r.data.project;
                setProject(p);
                setTranslated(p.translatedSections || []);
                setApproved(p.approvedSections || []);
                setConfidence(p.confidenceScores || []);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load project.');
                    setLoading(false);
                }
            });
    }, [token, id, dispatch, navigate]);

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/glossary', { headers: { Authorization: `Bearer ${token}` } })
            .then(async (r) => {
                const list = Array.isArray(r.data) ? r.data : (r.data.glossaries || []);
                const headers = { Authorization: `Bearer ${token}` };
                const enriched = await Promise.all(list.map(async (g) => {
                    try {
                        const tr = await axios.get(`/api/glossary/${g._id}/terms`, { headers });
                        return { glossary: g, terms: tr.data || [] };
                    } catch {
                        return { glossary: g, terms: [] };
                    }
                }));
                const flat = [];
                enriched.forEach(({ glossary, terms }) => {
                    terms.forEach((t) => flat.push({
                        source: t.source,
                        target: t.target,
                        context: t.context,
                        locked: !!t.approved && !t.ignore,
                        glossaryName: glossary.name,
                    }));
                });
                setAllGlossaryTerms(flat);
            })
            .catch(() => setAllGlossaryTerms([]));
    }, [token]);

    const sections = (project && project.sections) || [];
    const sourceLang = (project && project.sourceLanguage) || 'EN';
    const targetLang = (project && project.targetLanguage) || 'TGT';
    const total = sections.length;

    // Lookup TM matches for the active segment's source
    useEffect(() => {
        if (!sections[active] || !token) {
            setTmMatches([]);
            return;
        }
        setTmLoading(true);
        axios
            .post(
                '/api/translation-memory/check',
                {
                    userId: me._id,
                    originalText: sections[active],
                    sourceLanguage: sourceLang,
                    targetLanguage: targetLang,
                },
                { headers: { Authorization: `Bearer ${token}` } },
            )
            .then((r) => {
                const matches = Array.isArray(r.data) ? r.data : (r.data.matches || []);
                setTmMatches(matches.slice(0, 6).map((m) => ({
                    match: m.confidenceScore || m.match || 100,
                    src: m.originalText || m.source || '',
                    tgt: m.translatedText || m.target || '',
                    origin: m.origin || 'Memory',
                    when: '',
                })));
            })
            .catch(() => setTmMatches([]))
            .finally(() => setTmLoading(false));
    }, [active, sections, sourceLang, targetLang, token, me._id]);

    // When activating a segment, clear the prior AI suggestion
    useEffect(() => {
        setAiSuggestion(null);
        setAiLoading(false);
    }, [active]);

    const glossTermsForCurrent = useMemo(() => {
        const src = (sections[active] || '').toLowerCase();
        if (!src) return [];
        return allGlossaryTerms.filter((t) => src.includes((t.source || '').toLowerCase()));
    }, [sections, active, allGlossaryTerms]);

    const issues = useMemo(() => {
        const src = sections[active] || '';
        const tgt = translated[active] || '';
        const checks = [];
        if (!tgt.trim()) {
            checks.push({ kind: 'warn', title: 'Untranslated', desc: 'No target text yet — translate or accept an AI suggestion.' });
        } else {
            const ratio = src.length ? tgt.length / src.length : 1;
            if (ratio > 1.3) {
                checks.push({ kind: 'warn', title: `Length over source by ${Math.round((ratio - 1) * 100)}%`, desc: 'Target is much longer than source. May overflow UI containers.' });
            } else if (ratio < 0.6) {
                checks.push({ kind: 'warn', title: `Length under source by ${Math.round((1 - ratio) * 100)}%`, desc: 'Target is much shorter than source. Confirm intent.' });
            } else {
                checks.push({ kind: 'ok', title: 'Length within range', desc: 'Target length is within 30% of source.' });
            }
            const tagsSrc = (src.match(/\{[a-zA-Z_]+\}/g) || []);
            const tagsTgt = (tgt.match(/\{[a-zA-Z_]+\}/g) || []);
            const missing = tagsSrc.filter((t) => !tagsTgt.includes(t));
            if (missing.length) {
                checks.push({ kind: 'err', title: 'Tag mismatch', desc: `Missing placeholders: ${missing.join(', ')}.` });
            } else if (tagsSrc.length) {
                checks.push({ kind: 'ok', title: 'Tags &amp; placeholders preserved', desc: `${tagsSrc.length} token${tagsSrc.length === 1 ? '' : 's'} carried over correctly.` });
            }
            const conf = confidence[active];
            if (conf != null && conf < 70) {
                checks.push({ kind: 'warn', title: 'Low confidence', desc: `Score ${Math.round(conf)} — review carefully before approving.` });
            }
        }
        const glossMisses = glossTermsForCurrent.filter((g) => g.target && !tgt.includes(g.target));
        if (glossMisses.length === 0 && glossTermsForCurrent.length > 0) {
            checks.push({ kind: 'ok', title: 'Glossary terms intact', desc: `All ${glossTermsForCurrent.length} glossary token${glossTermsForCurrent.length === 1 ? '' : 's'} preserved.` });
        } else if (glossMisses.length > 0) {
            checks.push({ kind: 'warn', title: 'Glossary terms missing', desc: `Target is missing locked translation${glossMisses.length > 1 ? 's' : ''} for ${glossMisses.map((g) => g.source).join(', ')}.` });
        }
        return checks;
    }, [sections, translated, confidence, active, glossTermsForCurrent]);

    const currentComments = useMemo(
        () => (comments[active] || []),
        [comments, active],
    );

    const saveSegment = async (index, text, opts = {}) => {
        setSaving(true);
        try {
            const body = { index };
            if (text !== undefined) body.translatedText = text;
            if (opts.approved !== undefined) body.approved = opts.approved;
            await axios.put(`/api/projects/${id}/segment`, body, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (text !== undefined) {
                setTranslated((arr) => {
                    const next = arr.slice();
                    next[index] = text;
                    return next;
                });
            }
            if (opts.approved === true) setApproved((arr) => (arr.includes(index) ? arr : [...arr, index]));
            if (opts.approved === false) setApproved((arr) => arr.filter((x) => x !== index));
        } catch (e) {
            setError('Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const onChangeTarget = (index, val) => {
        setTranslated((arr) => {
            const next = arr.slice();
            next[index] = val;
            return next;
        });
    };

    const onSaveTarget = (index, val) => saveSegment(index, val);
    const onApprove = (index) => saveSegment(index, translated[index] || '', { approved: true });
    const onUnapprove = (index) => saveSegment(index, undefined, { approved: false });
    const onReject = (index) => saveSegment(index, '', { approved: false });

    const nextActive = useCallback(() => {
        setActive((a) => Math.min(total - 1, a + 1));
    }, [total]);

    const requestAi = useCallback(async () => {
        const src = sections[active];
        if (!src || !token) return;
        setAiLoading(true);
        try {
            const r = await axios.post(
                '/api/openai/translate',
                { text: src, sourceLanguage: sourceLang, targetLanguage: targetLang },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            const suggestion = r.data?.translation || r.data?.text || r.data?.result || r.data;
            setAiSuggestion(typeof suggestion === 'string' ? suggestion : null);
        } catch (e) {
            setAiSuggestion(null);
        } finally {
            setAiLoading(false);
        }
    }, [sections, active, token, sourceLang, targetLang]);

    const applyTM = useCallback((match) => {
        if (!match || !match.tgt) return;
        setTranslated((arr) => {
            const next = arr.slice();
            next[active] = match.tgt;
            return next;
        });
        saveSegment(active, match.tgt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    const addComment = (text) => {
        setComments((map) => {
            const list = map[active] || [];
            return {
                ...map,
                [active]: [
                    ...list,
                    {
                        who: `${me.firstName || ''} ${me.lastName || ''}`.trim() || me.email || 'You',
                        when: 'just now',
                        text,
                    },
                ],
            };
        });
    };

    // Keyboard shortcuts
    useEffect(() => {
        const onKey = (e) => {
            if (!project) return;
            const meta = e.metaKey || e.ctrlKey;
            if (meta && e.key === '/') {
                e.preventDefault();
                requestAi();
            } else if (meta && e.key === 'm') {
                e.preventDefault();
                if (tmMatches[0]) applyTM(tmMatches[0]);
            } else if (meta && e.key === '.') {
                e.preventDefault();
                nextActive();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [project, requestAi, applyTM, tmMatches, nextActive]);

    if (loading) return <div style={{ padding: 40 }} className="rs-app">Loading editor…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }} className="rs-app">{error}</div>;
    if (!project) return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg)' }} className="rs-app">
            <TopBar
                project={project}
                approvedCount={approved.length}
                total={total}
                current={active}
                sourceLang={(sourceLang || 'EN').toUpperCase()}
                targetLang={(targetLang || '').toUpperCase()}
                saving={saving}
                onBack={() => navigate(`/dashboard/projects/${id}`)}
            />
            <div className="rs-ed-body">
                <Navigator
                    sections={sections}
                    translated={translated}
                    approved={approved}
                    confidence={confidence}
                    active={active}
                    setActive={setActive}
                    filter={filter}
                    setFilter={setFilter}
                />
                <SegList
                    sections={sections}
                    translated={translated}
                    approved={approved}
                    confidence={confidence}
                    active={active}
                    setActive={setActive}
                    sourceLang={(sourceLang || 'EN').toUpperCase()}
                    targetLang={(targetLang || '').toUpperCase()}
                    glossTerms={glossTermsForCurrent}
                    aiSuggestion={aiSuggestion}
                    aiLoading={aiLoading}
                    onChangeTarget={onChangeTarget}
                    onSaveTarget={onSaveTarget}
                    onApprove={onApprove}
                    onUnapprove={onUnapprove}
                    onReject={onReject}
                    onAcceptAI={(text) => onSaveTarget(active, text)}
                    onDismissAI={() => setAiSuggestion(null)}
                    onRegenerateAI={requestAi}
                    nextActive={nextActive}
                />
                <ContextPanel
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    activeIndex={active}
                    tmMatches={tmMatches}
                    tmLoading={tmLoading}
                    glossTerms={glossTermsForCurrent}
                    comments={currentComments}
                    onAddComment={addComment}
                    issues={issues}
                    onApplyTM={applyTM}
                />
            </div>
            <FootBar />
        </div>
    );
};

export default RushEditor;
